import { useState } from 'react'

import { store } from 'redux/store'
import { useActionless, useAppSelector } from 'redux/hooks'
import { SET_FOLDER_STATE } from 'redux/folders/actions/types'
import { dropDraggedItem } from 'redux/folders/actions/helpers'

import { useIsFolderOwner } from 'components/folderPage/folderHooks'

import { DeckCardInfoType } from 'types/search'
import { DeckResponse } from 'services/apiTypes/deck.types'

import ToastService from 'services/toast.service'
import BookmarkService from 'services/bookmark.service'

import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import DeckPreviewOverlay from 'components/folderPage/DeckPreviewOverlay'
import DeckSettingsOverlay, { DeckSettingsContent } from 'components/deckPage/settings/DeckSettingsOverlay'
import QuickDeleteDeckModalConfirm from 'components/misc/QuickDeleteDeckModalConfirm'
import DeckDetailsFetchingOverlayWrapper from 'components/folderPage/DeckDetailsFetchingOverlayWrapper'
import SimpleSpinner from 'components/elements/SimpleSpinner'
import MoveToArbitraryFolderOverlay from 'components/folderPage/MoveToArbitraryFolderOverlay'
import { ExportOverlayContent } from 'components/deckPage/modals/Export'
import { CopyDeckContent } from 'components/deckPage/modals/CopyDeck'
import { ShareContent } from 'components/deckPage/header/Share'

import styles from './deckOptions.module.scss'

type Props = {
  deck: DeckCardInfoType
}

const DeckOptions = ({ deck }: Props) => {
  const isFolderOwner = useIsFolderOwner()

  const [loading, setLoading] = useState(false)

  const [deckDetailsOpen, setDeckDetailsOpen] = useState(false)

  const [deckSettingsOpen, setDeckSettingsOpen] = useState(false)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [cloneOverlayOpen, setCloneOverlayOpen] = useState(false)
  const [exportDeckOverlayOpen, setExportDeckOverlayOpen] = useState(false)
  const [shareOverlayOpen, setShareOverlayOpen] = useState(false)
  const [moveToArbitraryFolder, setMoveToArbitraryFolder] = useState(false)
  const [bookmarked, setBookmarked] = useState(deck?.bookmarked ?? false)

  const subfolders = useAppSelector(state => state.folders.rootFolder.subfolders)
  const parentFolder = useAppSelector(state => state.folders.rootFolder.parentFolder)

  const [setFolderState] = useActionless(SET_FOLDER_STATE)

  const handleCopyDeckNameToClipboard = () => {
    try {
      navigator?.clipboard?.writeText(deck.name)

      ToastService.create(`Copied deck name to clipboard`, 'Clipboard Helper', 'success')
    } catch {
      ToastService.create(`Unable to copy deck name to clipboard`, 'Clipboard Helper', 'error')
    }
  }

  const handleRemoveDeckFromFolderUI = () => {
    const { rootFolder } = store.getState().folders
    const updatedDecks = rootFolder.decks.filter(d => d.id !== deck.id)

    setFolderState({
      rootFolder: { ...rootFolder, decks: updatedDecks },
    })
  }

  const handleMoveItemToFolder = (folderId: number) => {
    setLoading(true)

    return dropDraggedItem({ type: 'deck', data: { id: deck.id } }, folderId)
      .then(handleRemoveDeckFromFolderUI)
      .catch(ToastService.create)
      .finally(() => setLoading(false))
  }

  const handleToggleBookmark = () => {
    if (!deck) return

    if (bookmarked)
      return BookmarkService.remove(deck.id)
        .then(() => ToastService.create('Bookmark removed', 'Bookmark service', 'success'))
        .then(() => setBookmarked(false))
        .catch(() => ToastService.create('Unable to delete bookmark', 'Bookmark service'))

    return BookmarkService.create(deck.id)
      .then(() => ToastService.create('Deck bookmarked', 'Bookmark service', 'success'))
      .then(() => setBookmarked(true))
      .catch(() => ToastService.create('Unable to create bookmark', 'Bookmark service'))
  }

  const handleUpdateDeckMetaData = (updatedDeck: DeckResponse) => {
    const { rootFolder } = store.getState().folders
    const updatedDecks = rootFolder.decks.map(deck => ({
      ...deck,
      name: deck.id === updatedDeck.id ? updatedDeck.name : deck.name,
      private: deck.id === updatedDeck.id ? updatedDeck.private : deck.private,
      unlisted: deck.id === updatedDeck.id ? updatedDeck.unlisted : deck.unlisted,
      theorycrafted: deck.id === updatedDeck.id ? updatedDeck.theorycrafted : deck.theorycrafted,
      deckFormat: deck.id === updatedDeck.id ? updatedDeck.deckFormat : deck.deckFormat,
    }))

    setFolderState({ rootFolder: { ...rootFolder, decks: updatedDecks } })
  }

  if (loading) return <SimpleSpinner size="xSmall" />

  return (
    <>
      <ArchidektDropdown
        menuClassName={styles.dropdownMenu}
        options={[
          { label: 'Deck preview', onClick: () => setDeckDetailsOpen(true), icon: 'chart line' },
          { type: 'spacer' },
          {
            label: 'Bookmark deck',
            onClick: handleToggleBookmark,
            icon: bookmarked ? 'bookmark' : 'bookmark outline',
            className: bookmarked ? styles.bookmarked : '',
          },
          { label: 'Deck settings', onClick: () => setDeckSettingsOpen(true), icon: 'cog', hidden: !isFolderOwner },
          { label: 'Export deck', onClick: () => setExportDeckOverlayOpen(true), icon: 'download' },
          { label: 'Clone deck', onClick: () => setCloneOverlayOpen(true), icon: 'copy' },
          { label: 'Delete deck', onClick: () => setConfirmDeleteOpen(true), icon: 'trash', hidden: !isFolderOwner },
          { type: 'spacer' },
          {
            label: 'Move deck to',
            icon: 'arrow right',
            type: 'extras',
            id: 'move-deck-to-other-dir',
            hidden: !isFolderOwner,
            options: [
              {
                label: 'Parent folder',
                onClick: () => handleMoveItemToFolder(parentFolder.id),
                hidden: !parentFolder?.id,
              },
              { type: 'spacer', hidden: !parentFolder?.id || !subfolders.length },
              ...subfolders.map(subfolder => ({
                label: subfolder.name,
                onClick: () => handleMoveItemToFolder(subfolder.id),
              })),
              { type: 'spacer', hidden: !parentFolder?.id },
              { label: 'View full folder tree', onClick: () => setMoveToArbitraryFolder(true), icon: 'folder open' },
            ],
          },
          { type: 'spacer', hidden: !isFolderOwner },
          { icon: 'play circle outline', to: `/playtester-v2/${deck?.id}`, label: 'Playtest deck' },
          { label: 'Share deck', onClick: () => setShareOverlayOpen(true), icon: 'share' },
          { label: 'Copy deck name', onClick: handleCopyDeckNameToClipboard, icon: 'copy' },
        ]}
      />

      <DeckPreviewOverlay deckId={deck.id} open={deckDetailsOpen} onClose={() => setDeckDetailsOpen(false)} />

      {/* Settings overlay that opens from the deckDetails overlay (2nd layer) - opened from the Deck header (eg: bracket settings button / tags) */}
      {deckDetailsOpen && <DeckSettingsOverlay />}

      <DeckDetailsFetchingOverlayWrapper
        open={deckSettingsOpen}
        onClose={() => setDeckSettingsOpen(false)}
        deckId={deck.id}>
        <DeckSettingsContent onDeleteDeck={handleRemoveDeckFromFolderUI} onDeckSaved={handleUpdateDeckMetaData} />
      </DeckDetailsFetchingOverlayWrapper>

      <DeckDetailsFetchingOverlayWrapper
        open={cloneOverlayOpen}
        onClose={() => setCloneOverlayOpen(false)}
        deckId={deck.id}>
        <CopyDeckContent onDeckCloned={() => setCloneOverlayOpen(false)} />
      </DeckDetailsFetchingOverlayWrapper>

      <DeckDetailsFetchingOverlayWrapper
        open={exportDeckOverlayOpen}
        onClose={() => setExportDeckOverlayOpen(false)}
        deckId={deck.id}>
        <ExportOverlayContent />
      </DeckDetailsFetchingOverlayWrapper>

      <DeckDetailsFetchingOverlayWrapper
        open={shareOverlayOpen}
        onClose={() => setShareOverlayOpen(false)}
        deckId={deck.id}>
        <ShareContent />
      </DeckDetailsFetchingOverlayWrapper>

      <QuickDeleteDeckModalConfirm
        deckId={deck?.id || 0}
        name={deck?.name || ''}
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onDeleted={() => handleRemoveDeckFromFolderUI()}
      />

      <MoveToArbitraryFolderOverlay
        open={moveToArbitraryFolder}
        onClose={() => setMoveToArbitraryFolder(false)}
        deckIds={[deck.id]}
      />
    </>
  )
}

export default DeckOptions
